<template>
  <!-- Jeśli dostępny jest niestandardowy szablon z endpointu (topProduct.template),
       renderujemy wygenerowany HTML -->
  <div v-if="customTemplate" v-html="generatedHtml"></div>
  <!-- W przeciwnym razie renderujemy domyślną strukturę -->
  <div v-else class="q-row products__promoted-product-container ">
    <div class="q-col-sm-12 products__promoted-product-container-inner ">
      <div class="products__promoted-product q-p-1 q-position-relative q-d-flex q-flex-wrap">
        <div class="products__promoted-product-image-container  q-col-4 q-col-md-12 q-p-0">
          <img class="products__promoted-product-image q-p-1"
               :src="staticImage" :alt="promotedProductTitle">
        </div>
        <div class="products__promoted-product-info q-col-8 q-col-md-12 q-px-2 q-text-black q-text-start ">
          <p class="products__promoted-product-title  q-m-0 q-fw-bold">
            {{ promotedProductTitle }}
          </p>
          <AutocompletePriceBox :product="promotedProductVariant"/>
          <a :href="actualLink" @click="handleClick" class="q-stretched-link"></a>
        </div>
        <a :href="actualLink" @click="handleClick" class="q-stretched-link"></a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AutocompletePriceBox from '@/components/AutocompletePopup/AutocompletePriceBox.vue';
import { defineProps, ref, inject, onUpdated, watch, computed } from 'vue';
import { generateStaticImage, addDisplayMacros, formatPrice, parsePrice, getDiscount, getRealPrice } from '@/core/utils';
import { createTemplate } from '@/core/Template';
import { SmartSearchConfigInterface } from '@/core/types';

// Pobieramy konfigurację – zakładamy, że endpoint zwraca obiekt topProduct zawierający m.in. productImageSize oraz szablon
const SmartSearchConfig = ref(inject<SmartSearchConfigInterface>('SmartSearchConfig')!);
const { width, height } = SmartSearchConfig.value.onfocus.topProduct.productImageSize;
const { useQuarticImages } = SmartSearchConfig.value.serp.productImageSize;

const customTemplate = SmartSearchConfig?.value?.serp?.customComponents?.topProduct || null;

const props = defineProps(['promotedProduct']);

// Inicjalizacja podstawowych danych
const promotedProductVariant = ref(props.promotedProduct.variants[0]);
const promotedProductTitle = ref(props.promotedProduct.title);
const actualLink = ref(props.promotedProduct.link);
const clickLink = ref(props.promotedProduct.link_click);
const productId = ref(props.promotedProduct.id);

// Obliczenie ścieżki do obrazu
const staticImage = computed(() => {
  const imageLink = promotedProductVariant.value.image_link;
  if (!useQuarticImages) {
    return imageLink;
  }
  return imageLink
      ? generateStaticImage(imageLink, width, height)
      : generateStaticImage('https://static.quartic.com.pl/img/no-image/no-image.png', width, height);
});

// Obsługa kliknięcia – ewentualna modyfikacja linku
const handleClick = () => {
  actualLink.value = clickLink.value;
};

onUpdated(() => {
  staticImage.value = useQuarticImages
      ? generateStaticImage(props.promotedProduct.variants[0].image_link, width, height)
      : props.promotedProduct.variants[0].image_link;
});

watch(() => props.promotedProduct, (newValue) => {
  actualLink.value = newValue.link;
  clickLink.value = newValue.link_click;
  promotedProductTitle.value = newValue.title;
  promotedProductVariant.value = newValue.variants[0];
  staticImage.value = useQuarticImages
      ? generateStaticImage(newValue.variants[0].image_link, width, height)
      : newValue.variants[0].image_link;
}, { deep: true });

// Obliczenia dodatkowych makr
const secondPrice = promotedProductVariant.value.old_price || promotedProductVariant.value.sale_price;
const { price, oldPrice } = getRealPrice(parsePrice(promotedProductVariant.value.price), parsePrice(secondPrice));
const omnibusPrice = parsePrice(promotedProductVariant.value.omnibus_price);
const formattedPrice = formatPrice(price, SmartSearchConfig.value.appConfiguration.currency, SmartSearchConfig.value.appConfiguration.removeDecimals);
const formattedPriceOld = formatPrice(oldPrice, SmartSearchConfig.value.appConfiguration.currency, SmartSearchConfig.value.appConfiguration.removeDecimals);
const formattedOmnibusPrice = formatPrice(omnibusPrice, SmartSearchConfig.value.appConfiguration.currency, SmartSearchConfig.value.appConfiguration.removeDecimals);
const discount = getDiscount(price, oldPrice);

// Funkcja przygotowująca czysty link (z zamianą protokołu)
const prepareClearLink = (link: string, clickLink: string) => {
  return ` href="${link}" data-href="${clickLink.replace(/^http:/, 'https:')}" onmousedown="return this.href = this.getAttribute('data-href');" ontouchstart="return this.href = this.getAttribute('data-href');" `;
};

// Jeśli jest dostępny niestandardowy szablon, rozszerzamy dane wariantu o makra i generujemy HTML
let generatedHtml = ref('');
if (customTemplate) {
  promotedProductVariant.value.discount = discount;
  promotedProductVariant.value.image_link_cdn = staticImage.value;
  promotedProductVariant.value.price_formatted = formattedPrice;
  promotedProductVariant.value.price_number = price;
  promotedProductVariant.value.old_price_formatted = formattedPriceOld;
  promotedProductVariant.value.old_price_number = oldPrice;
  promotedProductVariant.value.omnibus_price_formatted = formattedOmnibusPrice;
  promotedProductVariant.value.omnibus_price_number = omnibusPrice;
  promotedProductVariant.value.id = productId.value;
  promotedProductVariant.value.link_click = clickLink.value;
  promotedProductVariant.value.clear_link = prepareClearLink(actualLink.value, clickLink.value);

  // Dodatkowa modyfikacja danych
  addDisplayMacros(promotedProductVariant.value);

  // Generujemy HTML przy użyciu createTemplate – dla topProduct przyjmujemy, że nie mamy pętli, więc przekazujemy wariant jako główny produkt oraz jako jedyny element tablicy
  generatedHtml.value = createTemplate(customTemplate, promotedProductVariant.value, [promotedProductVariant.value]);
}
</script>

<style scoped>
img[lazy=loaded] {
  animation-duration: 400ms;
  animation-fill-mode: both;
  animation-name: fadeIn;
}

.products__promoted-product-image {
  height: auto;
  width: auto;
  margin: auto;
  min-width: 100px;
  max-width: 100%;
  max-height: 100%;
}

.products__promoted-product-title {
  text-align: center;
}

.products__promoted-product-container .single-product__price-box-container {
  text-align: center;
}

.products__promoted-product {
  display: flex !important;
  align-content: center;
  flex-wrap: wrap;
  flex-direction: column;
  min-height: 100%;
  justify-content: space-around;
}

.products__promoted-product-container {
  min-height: calc(100% - 50px);
}

@media (max-width: 768px) {
  .products__promoted-product-container {
    min-height: unset;
  }
  .products__promoted-product {
    align-content: unset;
    flex-wrap: unset;
    flex-direction: unset;
    min-height: unset;
    justify-content: unset;
  }
  .products__promoted-product-image {
    height: auto;
    width: auto;
    margin: auto;
    min-width: 100px;
    max-width: 100%;
    max-height: 135px;
  }
  .products__promoted-product {
    border-bottom: 1px solid #f0f2f6;
  }
  .products__promoted-product-title {
    text-align: left;
  }
  .products__promoted-product-container .single-product__price-box-container {
    text-align: left;
  }
}

.products__promoted-product {
  transition: background-color 0.3s ease;
}

.products__promoted-product:hover {
  background-color: #f2f2f2;
}

.products__promoted-product-title {
  font-size: 0.75rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
